/**
 * Tutoring (M3) — PURE logic for the submit→feedback→resubmit cycle (TS mirror of
 * `core_rust::use_cases::tutoring`). The server's write-path is the authority; this gates
 * the UI (the "resubmit" button, SLA/overdue calculation) with no network.
 */

/** Effective review policy (resolved from org defaults + override). */
export interface ReviewPolicy {
  allowResubmission: boolean;
  maxVersions: number;
  slaDays: number;
}

/** Fallback for when the org hasn't configured tutoring yet (== ReviewPolicy::FALLBACK). */
export const FALLBACK_POLICY: ReviewPolicy = {
  allowResubmission: true,
  maxVersions: 3,
  slaDays: 3,
};

export interface OrgDefaults {
  allowResubmission: boolean;
  maxVersions: number;
  slaDays: number;
}

export interface AssignmentOverride {
  allowResubmission?: boolean | null;
  maxVersions?: number | null;
  slaDays?: number | null;
}

function firstDefined<T>(over: T | null | undefined, base: T): T {
  return over === null || over === undefined ? base : over;
}

/**
 * Resolves the effective policy. Precedence: assignment override → org default
 * → fallback. The final project's SINGLE correction forces `maxVersions=1`
 * and `allowResubmission=false` (wins over everything).
 */
export function resolvePolicy(
  org: OrgDefaults | null | undefined,
  over: AssignmentOverride = {},
  finalProjectSingleCorrection = false,
): ReviewPolicy {
  const base: OrgDefaults = org ?? { ...FALLBACK_POLICY };
  const p: ReviewPolicy = {
    allowResubmission: firstDefined(over.allowResubmission, base.allowResubmission),
    maxVersions: Math.max(1, firstDefined(over.maxVersions, base.maxVersions)),
    slaDays: Math.max(0, firstDefined(over.slaDays, base.slaDays)),
  };
  if (finalProjectSingleCorrection) {
    p.allowResubmission = false;
    p.maxVersions = 1;
  }
  return p;
}

/** Number of the NEW version (the first is 1). */
export function nextVersion(currentMax: number | null | undefined): number {
  return (currentMax ?? 0) + 1;
}

export type ResubmitDenied = "not_allowed" | "previous_not_returned" | "max_versions_reached";

/**
 * Can the student create a NEW version? `latest` = last existing version
 * (`null` = none yet → first submission, always allowed).
 */
export function canResubmit(
  policy: ReviewPolicy,
  latest: { version: number; outcome: string | null } | null,
): { ok: true } | { ok: false; reason: ResubmitDenied } {
  if (!latest) {
    return { ok: true };
  }
  if (!policy.allowResubmission) {
    return { ok: false, reason: "not_allowed" };
  }
  if (latest.outcome !== "returned") {
    return { ok: false, reason: "previous_not_returned" };
  }
  if (nextVersion(latest.version) > policy.maxVersions) {
    return { ok: false, reason: "max_versions_reached" };
  }
  return { ok: true };
}

/** Grading deadline (SLA) of a pending version. */
export function slaDueAt(submittedAtISO: string, slaDays: number): Date {
  const d = new Date(submittedAtISO);
  d.setUTCDate(d.getUTCDate() + Math.max(0, slaDays));
  return d;
}

/** Is grading a pending version OVERDUE relative to the SLA? */
export function isOverdue(
  submittedAtISO: string,
  slaDays: number,
  now: Date = new Date(),
): boolean {
  return now.getTime() > slaDueAt(submittedAtISO, slaDays).getTime();
}

/** Cohort roles that can REVIEW (grade/return/approve) submissions. */
export function roleCanReview(role: string): boolean {
  return role === "lead" || role === "assistant" || role === "tutor";
}
